import { CircularProgress } from '@mui/material';
import { GenerateSvg } from '../../Svgs';
import './GenerateSchedule.scss';
import CustomInput from '../../../../../custom_components/CustomInput';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import MissionService from '../../../../../../../service/MissionService';
import useUrlParams from '../../../../../hooks/useUrlParams';
import useContacts from '../../../../../hooks/useContacts';
import {
  containsSpecialChars,
  getCharacterLimitMessage,
  getSpecialCharacterMessage,
  showToastMessage,
  validateNumberWithoutDecimal
} from '../../../../../../../utils/commonFunctions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Form } from 'react-bootstrap';
import DatetimeInput from '../../../../../../CommonComponents/DatetimeInput';
import CustomSelect from '../../../../../../CommonComponents/CustomSelect';
import { isMissingDateTime, START_TIME_REQUIRED_MESSAGE } from '../../../../../utils';

dayjs.extend(utc);

interface GenerateScheduleProps {
  open: boolean;
  close: () => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
}

type FormData = {
  name: string;
  task_plan: string;
  start_time: number | null;
  duration: string;
};

const GenerateSchedule = ({
  open,
  close,
  loading,
  setLoading
}: GenerateScheduleProps) => {
  // Extract URL parameters
  const { sat_grp_id, scenario_id, mission_id, sat_id } = useUrlParams();

  // Ref for modal container
  const formRef = useRef<HTMLDivElement>(null);

  // Custom hooks for API calls
  const { generate_schedule, generate_adhoc_task_schedule } = useContacts();
  
  // State for dropdowns
  const [taskPlans, setTaskPlans] = useState<{label: string; value: any}[]>([]);
  const [adhocTasks, setAdhocTasks] = useState<{label: string; value: any}[]>([]);

  // State for selected items
  const [selectedTaskPlan, setSelectedTaskPlan] = useState<any>(null);
  const [selectedAdhocTask, setSelectedAdhocTask] = useState<any>(null);

  // Source toggle state
  const [activeSource, setActiveSource] = useState<'task' | 'adhoc'>('task');
 
  // Reference UTC time
  const [refUTCTime, setRefUTCTime] = useState<number | null>(null);

  // Main form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    task_plan: '',
    start_time: null,
    duration: '360' // default duration
  });

  // DateTime validation state
  const [isDateTimeValid, setIsDateTimeValid] = useState(true);

  // Set default values when modal opens
  useEffect(() => {
    if (open && !formData?.start_time) {
      const utcNow = dayjs.utc(new Date()).valueOf();
      setFormData({
        ...formData,
        start_time: utcNow,
      });
      setRefUTCTime(utcNow);
      setActiveSource('task');
    }
  }, [open]);

  // Fetch Task Plans and Ad hoc Tasks
  useEffect(() => {
    if (!sat_grp_id) return;

    const fetchTaskPlans = async () => {
      try {
        const response = await MissionService.getAllTaskPlan(sat_grp_id);
        if (response?.data?.length) {
          setTaskPlans(
            response.data.map((tp: any) => ({
              label: tp.taskPlanName,
              value: tp
            }))
          );
        }
      } catch (error) {
        showToastMessage('error', 'Error fetching task plans');
      }
    };

    const fetchAdhocTasks = async () => {
      try {
        const response = await MissionService.getAllTask(sat_grp_id);
        const adhocTasks = response.data
          .filter((t: any) => t.taskType === "Adhoc")
          .map((t: any) => ({ label: t.taskName, value: t }));
        setAdhocTasks(adhocTasks);
      } catch (error) {
        console.error('Error fetching adhoc tasks:', error);
      }
    };

    fetchTaskPlans();
    fetchAdhocTasks();
  }, [sat_grp_id]);

  // Close form and reset state
  const closeForm = useCallback(() => {
    close();
    setFormData({
      name: '',
      task_plan: '',
      start_time: null,
      duration: '360'
    });
    setSelectedTaskPlan(null);
    setSelectedAdhocTask(null);
  }, [close]);

  // Handle text/number inputs with validation
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      
      if (name === 'name') {
        if (value.length > 26) {
          showToastMessage('info', getCharacterLimitMessage(26));
          return;
        }
        if (containsSpecialChars(value)) {
          showToastMessage('info', getSpecialCharacterMessage);
          return;
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    },
    []
  );

  // Calculate min/max UTC range for start time
  const calculateMaxTime = (range: string): number | null => {
    const startTimeDate = refUTCTime ? new Date(refUTCTime) : null;
    if (range === 'min' && startTimeDate) {
      return startTimeDate.getTime();
    } else if (range === 'max' && startTimeDate) {
      startTimeDate.setDate(startTimeDate.getDate() + 7);
      return startTimeDate.getTime();
    } else {
      return null;
    }
  };

  // Form submission
  const handleSubmit = useCallback(async () => {
    const { name, task_plan, start_time, duration } = formData;
    const durationNum = Number(duration);
    
    if (!name) return showToastMessage('error', 'Schedule Title is required');
    if (!task_plan) return showToastMessage('error', `${activeSource === 'task' ? 'Task Plan' : 'Ad hoc Task'} is required`);
    if (isMissingDateTime(start_time)) return showToastMessage('error', START_TIME_REQUIRED_MESSAGE);
    const startTime = Number(start_time);
    
    if (!duration || isNaN(durationNum)) {
      return showToastMessage('error', 'Invalid duration value');
    }
    if (durationNum < 10 || durationNum > 1440) {
      return showToastMessage('error', 'Duration must be between 10-1440 minutes');
    }
    
    if (
      calculateMaxTime("max") !== null && 
      calculateMaxTime("min") !== null && 
      (startTime > calculateMaxTime("max")! || startTime < calculateMaxTime("min")!)
    ) {
      return showToastMessage('error', "Start time must be within 7 days from the current UTC time");
    }

    const payload = {
      missionId: mission_id,
      scenarioId: scenario_id,
      satelliteId: sat_id,
      startEpoch: Math.floor(startTime / 1000),
      scheduleName: name,
      ...(activeSource === 'task'
        ? {
            taskPlanId: task_plan,
            duration: durationNum, 
          }
        : {
            adhocTaskId: task_plan,
          })
    };

    try {
      setLoading(true);
      if (activeSource === 'task') {
        await generate_schedule(payload);
      } else {
        await generate_adhoc_task_schedule(payload);
      }
      showToastMessage('success', 'Schedule generated successfully');
      closeForm();
    } catch (error: any) {
      console.error('Schedule generation failed:', error);
      showToastMessage('error', error.response?.data || 'Generation failed');
    } finally {
      setLoading(false);
    }
  }, [formData, activeSource, mission_id, scenario_id, sat_id, generate_schedule, generate_adhoc_task_schedule, setLoading, closeForm, refUTCTime]);

  // Toggle between Task Plan and Ad hoc Task
  const handleSourceChange = useCallback((source: 'task' | 'adhoc') => {
    setActiveSource(source);
    setSelectedTaskPlan(null);
    setSelectedAdhocTask(null);
    setFormData(prev => ({ ...prev, task_plan: '' }));
  }, []);

  // Select a Task/Ad hoc Task from dropdown
  const handleTaskSelect = useCallback((option: any, source: 'task' | 'adhoc') => {
    if (source === 'task') {
      setSelectedTaskPlan(option);
    } else {
      setSelectedAdhocTask(option);
    }
    setFormData(prev => ({
      ...prev,
      task_plan: option.value[source === 'task' ? 'taskPlanId' : 'taskId']
    }));
  }, []);

  // Wrapper style for toggle buttons
  const wrapperStyle = useMemo(() => ({
    display: 'flex',
    padding: '2px',
    alignItems: 'center',
    gap: '4px',
    background: '#222',
    borderRadius: '4px',
    border: '0.5px solid rgba(240, 240, 240, 0.20)'
  }), []);

  // Style function for active/inactive toggle buttons
  const buttonStyle = useMemo(() => (
    (isActive: boolean) => ({
      display: 'flex',
      padding: '4px 8px',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '4px',
      cursor: 'pointer',
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '12px',
      background: isActive ? 'rgba(204, 245, 78, 0.08)' : 'transparent',
      color: isActive ? 'rgba(204, 245, 78, 1)' : 'rgba(240, 240, 240, 0.6)',
      transition: 'background 0.2s, color 0.2s',
      border: 'none' 
    })
  ), []);

  if (!open) return null;

  const minStartTime = calculateMaxTime("min");
  const maxStartTime = calculateMaxTime("max");

  return (
    <div 
      className={`generate__schedule__container open`}
      onClick={e => e.stopPropagation()}
      ref={formRef}
    >
      {/* Header */}
      <div className='generate__schedule__container__header'>
        <div className='d-flex gap-2 align-items-center'>
          <GenerateSvg color='#CCF54E' />
          <span>Generate Schedule</span>
        </div>
        <button 
          className={`bg-transparent p-0 border-0 ${loading ? 'opacity-50' : ''}`}
          disabled={loading}
          onClick={closeForm}
        >
          <i className="fe fe-x fs-16" />
        </button>
      </div>

      {/* Body */}
      <div className='generate__schedule__container__body'>
        <div className='badge'>
          <i className="fe fe-info fs-16" /> Satellite details retrieved from TLE.
        </div>
        
        <div className='form__container'>
          {/* Schedule Title */}
          <div className='form__details'>
            <span>Schedule Title</span>
            <div style={{ width: "180px" }}>
              <CustomInput
                placeholder="Enter Schedule Title"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Source Selection (updated to semantic button) */}
          <div className='form__details'>
            <span>Source</span>
            <div style={wrapperStyle}>
              {/* Button for Task Plan */}
              <button
                type="button"
                style={buttonStyle(activeSource === 'task')}
                className='h-auto'
                onClick={() => handleSourceChange('task')}
              >
                Task Plan
              </button>
              {/* Button for Ad hoc Task */}
              <button
                type="button"
                className='h-auto'
                style={buttonStyle(activeSource === 'adhoc')}
                onClick={() => handleSourceChange('adhoc')}
              >
                Ad hoc Task
              </button>
            </div>
          </div>

          {/* Task/Adhoc selection */}
          <div className='form__details'>
            <span>{activeSource === 'task' ? 'Task Plan' : 'Ad hoc Task'}</span>
            <div style={{ width: "180px" }}>
              <CustomSelect
                options={activeSource === 'task' ? taskPlans : adhocTasks}
                placeholder={`Select ${activeSource === 'task' ? 'Task Plan' : 'Ad hoc Task'}`}
                value={activeSource === 'task' ? selectedTaskPlan : selectedAdhocTask}
                onChange={(option: any) => handleTaskSelect(option, activeSource)}
                placement='bottom'
                controllHeight='30px'
              />
            </div>
          </div>

          {/* Start Time & Duration */}
          <div className='d-flex w-100 flex-row gap-1'>
            <div className='d-flex flex-column gap-1 w-60'>
              <span className='fs-13'>Start Time</span>
              <div className='d-flex align-items-center gap-2'>
                <DatetimeInput
                  value={formData.start_time === null ? null : Math.floor(formData.start_time / 1000)}
                  minDateTime={minStartTime === null ? undefined : Math.floor(minStartTime / 1000)}
                  maxDateTime={maxStartTime === null ? undefined : Math.floor(maxStartTime / 1000)}
                  onChange={(value) => {
                    setFormData(prev => ({
                      ...prev,
                      start_time: value === null ? null : value * 1000
                    }));
                  }}
                  onValidationChange={(isValid) => {
                    setIsDateTimeValid(isValid);
                  }}
                />
              </div>
            </div>

            {activeSource === 'task' && (
              <div className='d-flex flex-column gap-1 w-40'>
                <span className='fs-13'>Duration</span>
                <div className='schedule__text__input__container bg-transparent w-100'>
                  <Form.Control
                    className="schedule__text__input"
                    name="duration"
                    type="number"
                    placeholder="Duration"
                    min={10}
                    max={1440}
                    value={formData.duration}
                    onChange={handleInputChange}
                    onKeyDown={validateNumberWithoutDecimal}
                    required
                  />
                  <span className='schedule__parameter__section'>min</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='generate__schedule__container__footer'>
        <button 
          className='close' 
          onClick={closeForm} 
          disabled={loading}
        >
          Close
        </button>
        <button
          className='generate'
          onClick={handleSubmit}
          disabled={loading || !isDateTimeValid}
        >
          {loading ? 'Generating' : 'Generate'}
          {loading && <CircularProgress size={12} className="text-white" />}
        </button>
      </div>
    </div>
  );
};

export default GenerateSchedule;
